import React from 'react'
import { FormattedMessage } from 'react-intl'
import { FormValidator, Box, Row } from '../../legacy-design-system/form'
import { Dialog, DialogFooter } from '../../legacy-design-system/modal'
import { Form } from './form-components/form'
import { ServerEnvironmentInput } from './form-components/server-environment-input'
import { LogLevelInput } from './form-components/log-level-input'
import { LogPathInput } from './form-components/log-path-input'
import { StacktraceCaptureModeInput } from './form-components/stacktrace-capture-mode-input'
import { BotBlockingInput } from './form-components/bot-blocking-input'
import { css } from '@emotion/css'
import { SamplingToggleInput } from './form-components/sampling-toggle-input'
import { SamplingBaselineInput } from './form-components/sampling-baseline-input'
import { SamplingFrequencyInput } from './form-components/sampling-frequency-input'
import { SamplingWindowInput } from './form-components/sampling-window-input'
import { SyslogToggleInput } from './form-components/syslog-toggle-input'
import { SyslogSettingsForm, SyslogSettingsFormState } from '../syslog'
import { CancelButton } from './form-components/cancel-button'
import { SaveButton } from './form-components/save-button'
import { EditedValues, ServerSettingsFormSetterFactory } from '.'
import { ServerSettingsFormValueBulk } from './server-settings-form-bulk-state-manager'
import {
  SamplingSettings,
  SAMPLING_OPTIONS,
  samplingProfileKey
} from './server-settings-form-single-view'
import { SplitContext } from '../../ng/splitio-wrapper'

export interface ServerSettingsFormBulkViewProps {
  readonly isOpen: boolean
  readonly formValue: ServerSettingsFormValueBulk
  readonly isAllowedAssess: boolean
  readonly isAllowedProtect: boolean
  readonly isAllowedSyslog: boolean
  readonly showLogPath: boolean
  readonly disableBotProtectToggle: boolean
  readonly disableSamplingToggle: boolean
  readonly disableSyslogToggle: boolean
  readonly showSampling: boolean
  readonly showSyslog: boolean

  readonly onCancel: () => void
  readonly onConfirm: () => void
  readonly formSetter: ServerSettingsFormSetterFactory
  readonly syslogOnChange: (value: SyslogSettingsFormState) => void

  readonly showBotProtectionEdit: boolean
  readonly showSamplingEdit: boolean
  readonly showSyslogEdit: boolean
  readonly disableShowBotProtectionEdit: () => void
  readonly disableShowSamplingEdit: () => void
  readonly disableShowSyslogEdit: () => void
  readonly setRefSamplingToggleInput: (ref: HTMLInputElement) => void
  readonly setRefBotProtectionToggle: (ref: HTMLInputElement) => void
  readonly setRefSyslogToggle: (ref: HTMLInputElement) => void
}

export function ServerSettingsFormBulkView({
  isOpen,
  formValue,
  isAllowedAssess,
  isAllowedProtect,
  isAllowedSyslog,
  showLogPath,
  disableBotProtectToggle,
  disableSamplingToggle,
  disableSyslogToggle,
  showBotProtectionEdit,
  showSamplingEdit,
  showSampling,
  showSyslogEdit,
  showSyslog,
  onCancel,
  onConfirm,
  wasEdited,
  formSetter,
  syslogOnChange,
  disableShowBotProtectionEdit,
  disableShowSamplingEdit,
  disableShowSyslogEdit,
  setRefSamplingToggleInput,
  setRefBotProtectionToggle,
  setRefSyslogToggle,
  // onReset,
  isValueEdited
}: ServerSettingsFormBulkViewProps & {
  readonly wasEdited: boolean
  // readonly onReset: () => void
  readonly isValueEdited: (key: keyof EditedValues) => boolean
}) {
  const [isCustomSamplingVisible, setIsCustomSamplingVisible] = React.useState(
    formValue?.[samplingProfileKey] === SAMPLING_OPTIONS.CUSTOM
  )
  // prettier-ignore
  const onSamplingChange = React.useCallback((option) => {
      setIsCustomSamplingVisible(option === SAMPLING_OPTIONS.CUSTOM)
    }, [])
  const ffAssessSamplingProfiles =
    // true ||
    React.useContext(SplitContext).isSplitTreatmentOn(
      'PROD-2881_assess_sampling_profiles'
    )

  const useLargerModal = showSampling || showSyslog
  const firstColumnWidth = useLargerModal ? 5 : 6
  const secondColumnWidth = useLargerModal ? 3 : 6
  const thirdColumnWidth = 4

  return (
    <FormattedMessage id="title_serversGrid_bulkSettings">
      {(title) => (
        <FormValidator value={formValue}>
          {({ getFormFieldProps, formIsValid }) => (
            <Dialog
              isOpen={isOpen}
              onCancel={onCancel}
              renderTitle={() => title}
              size="large"
            >
              <Form>
                <FormattedMessage id="detail_serversGrid_bulkSettings">
                  {(description) => <p>{description}</p>}
                </FormattedMessage>
                <Row>
                  <Box basis={firstColumnWidth}>
                    <ServerEnvironmentInput
                      value={formValue.environment}
                      edited={isValueEdited('environment')}
                      onChange={formSetter('environment')}
                    />
                  </Box>
                  <Box basis={secondColumnWidth}>
                    <LogLevelInput
                      value={formValue.logLevel}
                      edited={isValueEdited('logLevel')}
                      onChange={formSetter('logLevel')}
                    />
                  </Box>
                </Row>
                <Row>
                  {showLogPath && (
                    <Box basis={firstColumnWidth}>
                      <LogPathInput
                        value={formValue.logPath}
                        onChange={formSetter('logPath')}
                        getFormFieldProps={getFormFieldProps}
                      />
                    </Box>
                  )}
                  {isAllowedAssess && !ffAssessSamplingProfiles && (
                    <Box
                      basis={showLogPath ? secondColumnWidth : firstColumnWidth}
                    >
                      <StacktraceCaptureModeInput
                        value={formValue.stacktraceCaptureMode}
                        edited={isValueEdited('stacktraceCaptureMode')}
                        onChange={formSetter('stacktraceCaptureMode')}
                      />
                    </Box>
                  )}
                  {isAllowedProtect && (
                    <Box basis="content">
                      <BotBlockingInput
                        value={formValue.botProtection}
                        disabled={
                          disableBotProtectToggle || showBotProtectionEdit
                        }
                        disableBotProtectToggle={disableBotProtectToggle}
                        edited={isValueEdited('botProtection')}
                        onEdit={
                          showBotProtectionEdit
                            ? disableShowBotProtectionEdit
                            : undefined
                        }
                        onChange={formSetter('botProtection')}
                        setRef={setRefBotProtectionToggle}
                      />
                    </Box>
                  )}
                </Row>
                {!ffAssessSamplingProfiles && isAllowedAssess && (
                  <Row>
                    <Box>
                      <div
                        className={css`
                          padding: 16px 0px 2px;
                        `}
                      >
                        <SamplingToggleInput
                          disabled={disableSamplingToggle || showSamplingEdit}
                          onEdit={
                            showSamplingEdit
                              ? disableShowSamplingEdit
                              : undefined
                          }
                          disableSamplingToggle={disableSamplingToggle}
                          value={formValue.samplingEnabled}
                          onChange={formSetter('samplingEnabled')}
                          edited={isValueEdited('samplingEnabled')}
                          setRef={setRefSamplingToggleInput}
                        />
                      </div>
                    </Box>
                  </Row>
                )}
                {ffAssessSamplingProfiles && (
                  <SamplingSettings
                    formValue={formValue}
                    formSetter={formSetter}
                    onChange={onSamplingChange}
                  />
                )}
                {((showSampling && !ffAssessSamplingProfiles) ||
                  (ffAssessSamplingProfiles && isCustomSamplingVisible)) && (
                  <>
                    {isAllowedAssess && (
                      <Row>
                        <Box basis={firstColumnWidth}>
                          <StacktraceCaptureModeInput
                            value={formValue.stacktraceCaptureMode}
                            edited={false}
                            onChange={formSetter('stacktraceCaptureMode')}
                          />
                        </Box>
                      </Row>
                    )}
                    <Row>
                      <Box basis={firstColumnWidth}>
                        <SamplingBaselineInput
                          value={formValue.samplingBaseline}
                          onChange={formSetter('samplingBaseline')}
                          getFormFieldProps={getFormFieldProps}
                        />
                      </Box>
                      <Box basis={secondColumnWidth}>
                        <SamplingFrequencyInput
                          value={formValue.samplingFrequency}
                          onChange={formSetter('samplingFrequency')}
                          getFormFieldProps={getFormFieldProps}
                        />
                      </Box>
                      <Box basis={thirdColumnWidth}>
                        <SamplingWindowInput
                          value={formValue.samplingWindow}
                          onChange={formSetter('samplingWindow')}
                          getFormFieldProps={getFormFieldProps}
                        />
                      </Box>
                    </Row>
                  </>
                )}
                {isAllowedSyslog && (
                  <Row>
                    <Box>
                      <SyslogToggleInput
                        disabled={disableSyslogToggle || showSyslogEdit}
                        onEdit={
                          showSyslogEdit ? disableShowSyslogEdit : undefined
                        }
                        onChange={formSetter('syslogEnabled')}
                        value={formValue.syslogEnabled}
                        edited={isValueEdited('syslogEnabled')}
                        disableSyslogToggle={disableSyslogToggle}
                        setRef={setRefSyslogToggle}
                      />
                    </Box>
                  </Row>
                )}
                {showSyslog && (
                  <SyslogSettingsForm
                    state={formValue.syslog}
                    onChange={syslogOnChange}
                    getFormFieldProps={getFormFieldProps}
                  />
                )}
              </Form>
              <DialogFooter>
                <CancelButton onClick={onCancel} />
                <SaveButton
                  disabled={!formIsValid || !wasEdited}
                  onClick={onConfirm}
                />
              </DialogFooter>
            </Dialog>
          )}
        </FormValidator>
      )}
    </FormattedMessage>
  )
}
